/**
 * Qualified error message model, as returned from AVM-API and REVA-API.
 */
export interface QualifiedErrorMessage {
    error: string;
    error_de: string;
    error_code: string;
    error_code_description: string;
    error_code_description_de: string;
    info: string;
    info_de: string;
}

/**
 * Web Error class.
 */
export class WebError extends Error {
    readonly status: number;
    readonly json: any;
    readonly headers: Headers | null;

    /**
     * Create Web error from Response.
     * @param status response status code
     * @param json response JSON object (if available)
     * @param headers response headers (if available)
     */
    constructor(status: number, json?: any, headers?: Headers) {
        super(`Error ${status}`);
        this.status = status;
        this.json = json ?? null;
        this.headers = headers ?? null;
        // TODO: evaluate qualified error in constructor
    }

    /**
     * Check if error payload is a JSON.
     * Network- or Proxy-Errors (e.g. load balancers) can contain other error description (e.g. HTML).
     * @private
     */
    private hasJson(): boolean {
        return typeof this.json === "object" && this.json !== null;
    }

    /**
     * Get optional custom error message.
     * @return returns custom error from response JSON, or null
     */
    getCustomErrorMessage(): string | null {
        if (this.hasJson() && typeof this.json.error === "string") {
            return this.json.error;
        }
        return null;
    }

    /**
     * Get structured error (qualified error).
     * @return returns custom error from response JSON, or null
     */
    // TODO: Rename to getQualifiedError?
    getCustomErrorData(): QualifiedErrorMessage | null {
        if (
            this.hasJson() &&
            typeof this.json.error === "string" &&
            typeof this.json.error_de === "string" &&
            typeof this.json.error_code === "string"
        ) {
            return this.json;
        }
        return null;
    }

    /**
     * Get structured error code.
     * @return {string|null} returns error code from response JSON, or null
     */
    getErrorCode(): string | null {
        return this.getCustomErrorData()?.error_code ?? null;
    }
}

/**
 * Error if the response did not contain a valid JSON, although it was expected.
 */
export class InvalidResponseError extends WebError {}
